import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, CheckCircle2, ShieldCheck, TrendingUp } from "lucide-react";

const LENDERS = [
  { name: "Ally Bank", rate: "5.49% APR*", note: "Strong on luxury leases" },
  { name: "Chase Auto", rate: "5.79% APR*", note: "Conventional finance" },
  { name: "Capital One Auto", rate: "5.99% APR*", note: "Pre-qualification soft pull" },
  { name: "Bank of America", rate: "5.69% APR*", note: "Existing customer perks" },
  { name: "US Bank", rate: "5.89% APR*", note: "Used + CPO competitive" },
  { name: "BMW Financial", rate: "Captive*", note: "Manufacturer leases" },
];

export default function Financing() {
  const [price, setPrice] = useState(55000);
  const [down, setDown] = useState(3000);
  const [term, setTerm] = useState(60);
  const [apr, setApr] = useState(5.99);
  const [form, setForm] = useState({ name: "", email: "", phone: "", credit: "good", income: "" });
  const [submitting, setSubmitting] = useState(false);

  const monthly = useMemo(() => {
    const principal = Math.max(price - down, 0);
    const r = apr / 100 / 12;
    if (r === 0) return principal / term;
    return (principal * r) / (1 - Math.pow(1 + r, -term));
  }, [price, down, term, apr]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        type: "financing",
        payload: { ...form, price, down, term, apr, estimated_monthly: Math.round(monthly) },
      });
      toast.success("Pre-qualification request sent. Expect a call within 24 hours.");
      setForm({ name: "", email: "", phone: "", credit: "good", income: "" });
    } catch {
      toast.error("Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="financing-page">
      <p className="label-mono mb-3">Financing Concierge</p>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-5">Smart money.<br />Better cars.</h1>
      <p className="text-gray-400 max-w-2xl mb-12">Pre-qualify with our lender network in minutes. See your real monthly payment before you decide on the car.</p>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Calculator */}
        <div className="lg:col-span-7 glass rounded-2xl p-7 md:p-10" data-testid="financing-calculator">
          <div className="flex items-center gap-2 mb-6">
            <Calculator className="w-4 h-4 text-silver" />
            <div className="label-mono">Monthly Payment Calculator</div>
          </div>

          <div className="space-y-7">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-300">Vehicle price</div>
                <div className="font-display text-lg">${price.toLocaleString()}</div>
              </div>
              <Slider min={15000} max={250000} step={1000} value={[price]} onValueChange={(v) => setPrice(v[0])} data-testid="calc-price" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-300">Down payment</div>
                <div className="font-display text-lg">${down.toLocaleString()}</div>
              </div>
              <Slider min={0} max={50000} step={500} value={[down]} onValueChange={(v) => setDown(v[0])} data-testid="calc-down" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-300">Term (months)</div>
                <div className="font-display text-lg">{term}</div>
              </div>
              <Slider min={24} max={84} step={12} value={[term]} onValueChange={(v) => setTerm(v[0])} data-testid="calc-term" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-gray-300">APR</div>
                <div className="font-display text-lg">{apr.toFixed(2)}%</div>
              </div>
              <Slider min={2} max={15} step={0.1} value={[apr]} onValueChange={(v) => setApr(v[0])} data-testid="calc-apr" />
            </div>
          </div>

          <div className="mt-10 border-t border-white/10 pt-7 grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="label-mono mb-2">Estimated Monthly</div>
              <div className="font-display text-3xl">${Math.round(monthly).toLocaleString()}</div>
            </div>
            <div>
              <div className="label-mono mb-2">Total Interest</div>
              <div className="font-display text-lg">${Math.round(monthly * term - (price - down)).toLocaleString()}</div>
            </div>
            <div>
              <div className="label-mono mb-2">Total Cost</div>
              <div className="font-display text-lg">${Math.round(monthly * term + down).toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Pre-qual form */}
        <form onSubmit={submit} className="lg:col-span-5 glass rounded-2xl p-7 md:p-10 space-y-3" data-testid="prequal-form">
          <p className="label-mono mb-1">Pre-qualification</p>
          <h3 className="font-display text-2xl mb-5">Soft-pull, no commitment.</h3>
          <Input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="prequal-name" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="prequal-email" />
          <Input required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="prequal-phone" />
          <select value={form.credit} onChange={(e) => setForm({ ...form, credit: e.target.value })} className="w-full bg-[#0A0A0E] border border-[#1A1A24] h-12 rounded-xl text-white px-3 text-sm" data-testid="prequal-credit">
            <option value="excellent">Excellent (740+)</option>
            <option value="good">Good (680-739)</option>
            <option value="fair">Fair (620-679)</option>
            <option value="building">Rebuilding (&lt;620)</option>
          </select>
          <Input placeholder="Annual income ($)" value={form.income} onChange={(e) => setForm({ ...form, income: e.target.value })} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="prequal-income" />
          <button type="submit" disabled={submitting} className="w-full silver-gradient text-[#050507] font-bold py-3.5 rounded-full disabled:opacity-50 mt-2" data-testid="prequal-submit">
            {submitting ? "Sending…" : "Get Pre-Qualified"}
          </button>
          <p className="text-[10px] text-gray-500 leading-relaxed mt-3">No hard credit pull. We match you to lenders in our network and your broker walks through options.</p>
        </form>
      </div>

      {/* Lenders */}
      <div className="mt-16">
        <p className="label-mono mb-5">Lender Partners</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LENDERS.map((l) => (
            <div key={l.name} className="bg-[#0A0A0E] border border-[#1A1A24] p-6 rounded-2xl">
              <div className="font-display text-lg mb-1">{l.name}</div>
              <div className="text-sm text-silver mb-2">{l.rate}</div>
              <p className="text-xs text-gray-400">{l.note}</p>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-gray-500 mt-4">*Indicative rates. Actual APR depends on credit, vehicle, term, and lender underwriting.</p>
      </div>
    </div>
  );
}
